import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { clientQuery, clientMutation } from 'Core/data/GraphQLApi';
import { ExceptionManager } from 'Core/logManager';
import { endpoints } from 'Core/defaultValues';
import { resetALL } from '../../../../../.globals/redux/actions';

const ROUTE = 'components/Map/components/ClipRequestDrawerMap/redux/slice';

const MARKER_DETAIL_TRAIL_QUERY = `
  query getMarkerDetailTrail($type: MarkerType!, $id: String!, $unitId: String!) {
    Marker: getMarkerDetail(type: $type, id: $id, unitId: $unitId) {
      id
      type
      markerDetail {
        ... on MarkerDetailTrail {
          vehicleTrail {
            id
            deviceId
            latitude
            longitude
            heading
            eventCode
            eventName
            address
            formattedAddress
            driverId
            driverName
            ecuEngineOdometer
            ecuSpeed
            ecuEngineHours
            engineHours
            engineOdometer
            speed
            ignitionStatus
            satelliteCount
            signal
            speedLimit
            unitTime
            state
            unitType
            zip
            hDop
            vDop
            deviceData
          }
          unit {
            id
            deviceCategory
            useEcuOdometer
            useEcuEngineHours
            useEcuSpeed
            label
            jbus
            vbus
            typeId
            offsetEcuEngineOdometer
            offsetEcuEngineHours
            offsetEngineOdometer
            offsetEngineHours
            notes
            lastReading {
              deviceLabel
              temperatures
              dtc
              unitType
              landmark_id
              LastMessageTime
              satelliteCount
              deviceData
              hDop
              sdCard
            }
            esn
            unitVersionProduct
            asset {
              vin
              make
              model
              year
            }
            dtcCount {
              history
              active
            }
            timeZoneId
            timeZone {
              id
              name
            }
            crews {
              id
              firstName
              lastName
            }
            tags {
              id
              label
              color
              path
            }
          }
          linkedDevice {
            id
            esn
            deviceModelId
            deviceModelName
            manufacturerId
            manufacturerName
            simProviderName
            hasVideo
            liveMode
            firmware
            firmwareId
            label
          }
        }
      }
    }
  }
`;

const SNAPSHOT_QUERY = `
  query getVideoLastSnapshot($deviceId: String!, $unitTime: AWSDateTime!) {
    Snapshot: getVideoLastSnapshot(deviceId: $deviceId, unitTime: $unitTime) {
      id
      deviceId
      eventCode
      eventName
      thumbnailUrl
      duration
      mediaType
      url
      unitTime
    }
  }
`;

const CREATE_CLIP_REQUEST_MUTATION = `
  mutation createClipRequest (
    $dateRange: AWSDateTimeRange!,
    $deviceId: String!,
    $thumbnailUrl: String,
    $transactionId: String,
    $tripID: String,
    $mp4Url: String,
    $rotate: String,
    $clipID: String,
    $latitude: Float,
    $longitude: Float,
    $isPrivate: Boolean
  ) {
    createClipRequest (
      deviceId: $deviceId,
      dateRange: $dateRange,
      thumbnailUrl: $thumbnailUrl,
      transactionId: $transactionId,
      tripID: $tripID,
      mp4Url: $mp4Url,
      rotate: $rotate,
      clipID: $clipID,
      latitude: $latitude,
      longitude: $longitude,
      isPrivate: $isPrivate
    ) {
      id
      accountId
      createdOn
      deviceId
      label
      status
      time
      tripID
      clipID
      rotate
      url
      startDate
      endDate
      notes
      starred
      errorMsg
      deviceModelId
      deviceModelName
      unitId
      unit {
        id
        label
      }
    }
  }
`;

const INIT_STATE = {
  event: {
    id: null,
    unitId: null,
    lat: null,
    lng: null,
    unitTime: null,
  },
  data: null,
  loading: false,
  error: false,
  loadingSnapshot: false,
  snapshot: null,
  errorSnapshot: false,
  loadingCreate: false,
  createResult: null,
  errorCreate: false,
};


export const getClipDrawerRequestData = createAsyncThunk(
  'clipRequestDrawerMap/getClipDrawerRequestData',
  async ({ id, unitId }, { rejectWithValue }) => {
    try {
      const body = {
        type: 'Trail',
        id,
        unitId: `${unitId}`,
      };

      const data = await clientQuery(
        MARKER_DETAIL_TRAIL_QUERY,
        body,
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (data && data.Marker) {
        return data.Marker;
      }
      return rejectWithValue({ error: true });
    } catch (exc) {
      ExceptionManager(exc, ROUTE, 'getClipDrawerRequestData');
      return rejectWithValue({ error: true });
    }
  }
);

export const getClipDrawerSnapshotData = createAsyncThunk(
  'clipRequestDrawerMap/getClipDrawerSnapshotData',
  async (payload, { rejectWithValue }) => {
    try {
      const { deviceId, unitTime } = payload;

      const data = await clientQuery(
        SNAPSHOT_QUERY,
        {
          deviceId,
          unitTime,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (data && data.Snapshot) {
        return {
          ...data.Snapshot,
          pictureID: data.Snapshot.id,
        };
      }
      return {};
    } catch (exc) {
      ExceptionManager(exc, ROUTE, 'getClipDrawerSnapshotData');
      return rejectWithValue({ error: true });
    }
  }
);

export const createClipRequestFromDrawer = createAsyncThunk(
  'clipRequestDrawerMap/createClipRequestFromDrawer',
  async (payload, { rejectWithValue }) => {
    try {
      const data = await clientMutation(
        CREATE_CLIP_REQUEST_MUTATION,
        payload,
        endpoints.GRAPHQL_GENERAL
      );

      if (data && data.createClipRequest) {
        return data.createClipRequest;
      }
      return rejectWithValue({ error: true });
    } catch (exc) {
      if (!payload?.hideNotification) {
        ExceptionManager(exc, ROUTE, 'createClipRequest');
      }

      let message = '';
      let errorCode = 0;
      if (exc.errorData && exc.errorData.errors) {
        exc.errorData.errors.forEach((error) => {
          if (error.errorInfo && error.errorInfo.code) {
            errorCode = error.errorInfo.code;
            switch (error.errorInfo.code) {
              case 1011:
                message = 'infoBubble_cameraNotFound';
                break;
              case 1001:
                message = 'infoBubble_requestRangeTimeError';
                break;
              default:
                message = 'errorMessage';
                break;
            }
          }
        });
      }

      return rejectWithValue({ error: true, message, errorCode });
    }
  }
);

const clipRequestDrawerMapSlice = createSlice({
  name: 'clipRequestDrawerMap',
  initialState: INIT_STATE,
  reducers: {
    setClipRequestData(state, action) {
      const { event } = action.payload ?? {};
      const { id, unitId, lat, lng, unitTime } = event ?? {};
      state.event.id = id;
      state.event.unitId = unitId;
      state.event.lat = lat;
      state.event.lng = lng;
      state.event.unitTime = unitTime ?? null;
    },
    resetClipRequestDrawer(state, action) {
      return action.payload ? { ...state, ...action.payload } : { ...INIT_STATE };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(resetALL, () => INIT_STATE);
    builder
      .addCase(getClipDrawerRequestData.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getClipDrawerRequestData.fulfilled, (state, action) => {
        state.loading = false;
        state.error = false;
        state.data = action.payload;
      })
      .addCase(getClipDrawerRequestData.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
        state.data = action.payload ?? { error: true };
      })
      .addCase(getClipDrawerSnapshotData.pending, (state) => {
        state.loadingSnapshot = true;
        state.errorSnapshot = false;
        state.snapshot = null;
      })
      .addCase(getClipDrawerSnapshotData.fulfilled, (state, action) => {
        state.loadingSnapshot = false;
        state.errorSnapshot = false;
        state.snapshot = action.payload;
      })
      .addCase(getClipDrawerSnapshotData.rejected, (state, action) => {
        state.loadingSnapshot = false;
        state.errorSnapshot = true;
        state.snapshot = action.payload ?? { error: true };
      })
      .addCase(createClipRequestFromDrawer.pending, (state) => {
        state.loadingCreate = true;
        state.errorCreate = false;
        state.createResult = null;
      })
      .addCase(createClipRequestFromDrawer.fulfilled, (state, action) => {
        state.loadingCreate = false;
        state.errorCreate = false;
        state.createResult = action.payload;
      })
      .addCase(createClipRequestFromDrawer.rejected, (state, action) => {
        state.loadingCreate = false;
        state.errorCreate = true;
        state.createResult = action.payload ?? { error: true };
      });
  },
});

export const { actions, reducer } = clipRequestDrawerMapSlice;
export const { setClipRequestData, resetClipRequestDrawer } = actions;
export default reducer;
